<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Remitos de compra</h1>
          <p class="view-header__subtitle">Gestioná los remitos de compra recibidos.</p>
        </div>
        <div class="view-header__actions">
          <router-link :to="nuevoRemitoDeCompraLink" class="btn view-header__btn view-header__btn--primary">
            <i class="bi bi-plus-lg"></i>
            Nuevo remito
          </router-link>
        </div>
      </div>
    </div>
    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar...">
          </div>
          <div class="filters-panel__field filters-panel__field--inline">
            <label class="control-label">Fecha:</label>
            <Daterange ref="daterange" :opendir="'left'" :initial-start="dateRangeStart" :initial-end="dateRangeEnd" @datepicked="onDatePicked" />
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="clearFilters">
              <i class="bi bi-eraser"></i>
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
        @shippingPurchaseDetail="getShippingPurchaseDetail"
        @delete="deleteShippingPurchase"
      />
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import Daterange from "../../global/Daterange-component.vue";
import formatDateMixin from "../../../mixins/formatDateMixin";

export default {
  components: {
    VdtnetTable,
    Daterange,
  },
  mixins: [formatDateMixin],
  data: function () {
    const defaultRange = this.last30DaysRange();
    return {
      doSearch: '',
      isLoading: false,
      dateRangeStart: defaultRange.startDate,
      dateRangeEnd: defaultRange.endDate,
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/shippingpurchases/list",
          type: 'GET',
          data: (d) => {
            d.daterange = this.daterange;
            return d;
          },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        },
        processing: false,
        pageLength: 15,
        searching: true,
        searchDelay: 1500,
        destroy: true,
        ordering: true,
        lengthChange: true,
        fixedHeader: true,
        autoWidth: false,
      },
      fields: {
        number: { label: "N°", width: "80px" },
        created_at: {
          label: "Fecha",
          width: "130px",
          render: (data) => new Date(data).toLocaleDateString('es-AR'),
        },
        provider: {
          label: "Proveedor",
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        user: { label: "Comprador", width: "150px" },
        actions: {
          isLocal: true,
          label: "Acciones",
          width: "100px",
          align: "right",
          defaultContent: `
            <div class="table-action-group">
              <button data-action="shippingPurchaseDetail" class="table-action-btn" type="button" title="Ver detalle">
                <i class="bi bi-file-earmark-text"></i>
              </button>
              <button data-action="delete" class="table-action-btn" type="button" title="Eliminar comprobante">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          `,
        },
      },
    };
  },
  computed: {
    daterange() {
      if (!this.dateRangeStart || !this.dateRangeEnd) return null;
      return `${this.formatoFecha2Daterange(this.dateRangeStart)} - ${this.formatoFecha2Daterange(this.dateRangeEnd)}`;
    },
    nuevoRemitoDeCompraLink() {
      return { name: 'Nuevo remito de compra' };
    },
  },
  mounted() {
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  watch: {
    doSearch(newValue, oldValue) {
      if (newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch);
      }
      if (newValue === '' && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch);
      }
    },
    daterange() {
      this.refreshTable();
    },
  },
  methods: {
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    onDatePicked(date) {
      this.dateRangeStart = date.startDate;
      this.dateRangeEnd = date.endDate;
    },
    clearFilters() {
      this.doSearch = '';
      const defaultRange = this.last30DaysRange();
      this.dateRangeStart = defaultRange.startDate;
      this.dateRangeEnd = defaultRange.endDate;
    },
    getShippingPurchaseDetail(data) {
      this.$router.push({
        name: "Detalle de remito de compra",
        params: { shippingPurchaseId: data.id },
      });
    },
    deleteShippingPurchase(data) {
      if (!confirm("¿Está seguro que desea borrar este comprobante?")) return;
      this.$http.post("/api/shippingpurchases/delete", { id: data.id }).then((response) => {
        if (response.data.code === 201) this.refreshTable();
      });
    },
  },
};
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>
